/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

/*
 * If the token swap failed in Authorize lambda then we logout before continuing PKCE
 */

import { Auth } from "aws-amplify";
import jwt_decode from "jwt-decode";

import { QUERY_PARAMS_KEYS, STORAGE_KEYS } from "../const";
import { eraseAllCookies, getCookie } from "./cookieHelper";

/*
 * Describe the (already-rejected) access token cookie using only non-sensitive claims.
 * Used purely to understand, in the field, how a customer reached the revoked-token state:
 * which client, how old the session was, and whether the token was actually time-expired
 * or still-valid-but-revoked. Never returns the token itself or any PII (no sub/email).
 */
const describeAccessTokenCookie = () => {
	const accessToken = getCookie("access_token");
	if (!accessToken) {
		return { accessTokenPresent: false };
	}
	try {
		const claims = jwt_decode(accessToken);
		const nowSeconds = Math.floor(Date.now() / 1000);
		// Flat keys (no nested object) so the whole log line stays visible and
		// copy-pasteable from the browser console — nested objects collapse to "{…}".
		return {
			accessTokenPresent: true,
			accessTokenTokenUse: claims.token_use,
			accessTokenClientId: claims.client_id,
			accessTokenIat: claims.iat,
			accessTokenExp: claims.exp,
			accessTokenAuthTime: claims.auth_time,
			accessTokenAgeSeconds: claims.iat ? nowSeconds - claims.iat : null,
			accessTokenTimeExpired: claims.exp ? nowSeconds >= claims.exp : null,
		};
	} catch (error) {
		return { accessTokenPresent: true, accessTokenDecodeError: true };
	}
};

const checkForceAuth = async () => {
	// Only the literal "true" triggers a forced sign-out. Authorize sets forceAuth=true;
	// treating any non-empty value as truthy would let a crafted URL force a sign-out and
	// would make the log message below inaccurate.
	const forceAuth =
		new URLSearchParams(window.location.search).get(
			QUERY_PARAMS_KEYS.forceAuth
		) === "true";

	if (forceAuth) {
		const queryStringParams = new URLSearchParams(window.location.search);

		/*
		 * forceAuth means Authorize refused the fast-SSO token swap (typically because the
		 * access token was revoked upstream). We cannot see what revoked it, so log the
		 * victim's context to surface real-world patterns — which client initiated the flow,
		 * how stale the session was, and expired-vs-revoked — before we clear everything.
		 */
		// Serialize to a string at log time: checkForceAuth redirects (signOut) immediately
		// after this, and DevTools reads an object's expanded tree lazily — by then the page
		// has navigated and the reference is stale ("No properties"). A string is captured now.
		console.log(
			"[checkForceAuth] forceAuth=true — fast-SSO swap was refused upstream " +
				JSON.stringify({
					client_id: queryStringParams.get(
						QUERY_PARAMS_KEYS.clientID
					),
					hasIdTokenCookie: Boolean(getCookie("id_token")),
					hasRefreshTokenCookie: Boolean(getCookie("refresh_token")),
					hasAuthCode: Boolean(
						queryStringParams.get(QUERY_PARAMS_KEYS.authCode)
					),
					...describeAccessTokenCookie(),
				})
		);

		// Preserve the interrupted PKCE request across the OAuth sign-out redirect so the
		// flow resumes on the login page instead of dead-ending at /app-logout. signOut()
		// navigates to a static /logout with no query params, but redirectInfo survives it
		// (cleanLocalStorage never touches it) and Logout reads it first on arrival.
		const clientID = queryStringParams.get(QUERY_PARAMS_KEYS.clientID);
		const redirectURI = queryStringParams.get(
			QUERY_PARAMS_KEYS.redirectURI
		);
		const authCode = queryStringParams.get(QUERY_PARAMS_KEYS.authCode);
		const state = queryStringParams.get(QUERY_PARAMS_KEYS.state);
		if (clientID && redirectURI && authCode) {
			localStorage.setItem(
				STORAGE_KEYS.redirectInfo,
				JSON.stringify({
					resumeAuth: true,
					clientID,
					redirectURI,
					authCode,
					state,
				})
			);
		}

		eraseAllCookies();

		localStorage.removeItem(STORAGE_KEYS.clientID);

		await Auth.signOut();

		return true;
	}

	return false;
};

export default checkForceAuth;
